<template>
  <div>
    <v-data-table
      v-on:current-items="handleCurrentItemsChange"
      v-model="selectedItems"
      multi-sort
      show-select
      show-expand
      :headers="headers"
      :items="getTimePairsOrAdjustedTimePairs(getFilteredTimes(getApprovalsByUsername(username).summaries))"
      :items-per-page="getTimePairsPerPage()"
      :no-data-text="`${$t('tables.noData')}`"
      :mobile-breakpoint="$vuetify.breakpoint.mobileBreakpoint"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :footer-props="getTableFooterProps()"
      id="singleApprovalTimePairTable"
      class="elevation-1 mb-5"
    >
      <template v-slot:header.data-table-select></template>

      <template v-slot:item.data-table-expand="{ expand, item, isExpanded }">
        <v-tooltip top :disabled="isMobile()">
          <template v-slot:activator="{ on }">
            <span
              v-if="isMobile()"
              class="grey--text"
            >
              {{ isExpanded ? 'Hide' : 'Show' }} Additional Time Data
            </span>
            <v-btn
              v-on="on"
              icon
              :disabled="(isMobile() || item.segmentTimePairs.length === 0)
                         && item.startingWorkScheduleDay == null"
              @click="expand(!isExpanded)"
            >
              <v-icon>
                {{ isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ isExpanded ? $t('indicators.hideAdditionalData') : $t('indicators.showAdditionalData') }} </span>
        </v-tooltip>
      </template>
      <template v-slot:item.data-table-select="{ item }">
        <v-checkbox
          v-if="isTimePairApprovalFeatureActive && !viewAdjustedTime"
          v-model="timePairSelectability[item.id].selected"
          ripple
          hide-details
          class="mt-0 ml-2 pt-0"
          :disabled="!item.clockOut || !timePairNotApprovedOrRejected(item.status)"
          @change="handleTimePairSelection(item, $event)"
        />
        <v-checkbox
          v-if="isTimePairApprovalFeatureActive && viewAdjustedTime"
          v-model="timePairSelectability[item.clockOut == null ? item.id : item.originalVersionId].selected"
          ripple
          hide-details
          class="mt-0 ml-2 pt-0"
          :disabled="!item.clockOut || !timePairNotApprovedOrRejected(item.status)"
          @change="handleTimePairSelection(item, $event)"
        />
      </template>
      <template v-slot:item.approvals="{ item }" v-if="isTimePairApprovalFeatureActive">
        <v-btn
          v-show="!!item.clockOut && timePairNotApprovedOrRejected(item.status)"
          small
          color="error"
          class="mx-2"
          @click="singleTimePairApproval(item, false)"
        >
          <v-icon>clear</v-icon>
        </v-btn>
        <v-btn
          v-show="!!item.clockOut && timePairNotApprovedOrRejected(item.status)"
          small
          color="success"
          class="ml-2"
          @click="singleTimePairApproval(item, true)"
        >
          <v-icon>done</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.info="{ item }">
        <TimePairStatusIndicator :items="getRowIndicators(item)"/>
      </template>
      <template v-slot:item.position="{ item }">
        {{ item.employeeAssignment.position.description ?  item.employeeAssignment.position.description
        : `(${item.employeeAssignment.position.id})`}}
      </template>
      <template v-slot:item.startDate="{ item }">
        <slot name="startDate" v-bind:item="item">
          <StartDateTableCell :item="item"/>
        </slot>
      </template>
      <template v-slot:item.clockIn="{ item }">
        <slot name="clockIn" v-bind:item="item">
          <StartTimeTableCell :item="item"/>
        </slot>
      </template>
      <template v-slot:item.clockOut="{ item }">
        <div v-if="!item.clockOut">
          {{ ' - ' }}
        </div>
        <template v-else>
          <EndTimeTableCell :item="item" badge/>
        </template>
      </template>
      <template v-slot:item.duration="{ item }">
        <v-tooltip bottom :disabled="!showTimePairDeviation(item, true)">
          <template v-slot:activator="{ on }">
            <v-badge
              dot
              :value="showTimePairDeviation(item, true)"
              color="var(--v-warning-lighten1)"
            >
              <span v-on="on">
                {{ convertToDisplayedDuration(item) }}
              </span>
            </v-badge>
          </template>
          {{ getWorkScheduleDeviation(item) }}
        </v-tooltip>
      </template>
      <template v-slot:item.expectedDuration="{ item }">
        <v-tooltip top :disabled="item.startingWorkScheduleDay == null">
          <template v-slot:activator="{ on }">
            <v-icon small>calendar_today</v-icon>
            <span v-on="on">
              {{ getExpectedDuration(item) }}
            </span>
          </template>
          {{ $t('workSchedules.ws') }}: {{ getWorkScheduleExternalNameByCode(item.workScheduleExternalCode) }}
        </v-tooltip>
      </template>
      <template v-slot:item.status="{ item }">
        <ApprovalStatusTableCell :item="item"/>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip
          v-if="timePairNotApprovedOrRejected(item.status)"
          top
          :disabled="isMobile()"
        >
          <template v-slot:activator="{ on }">
            <v-icon
              v-if="isSegmentTimePairFeatureActive && isTimePairAddFeatureActive"
              v-on="on"
              v-show="!!item.clockOut && !item.isInactive"
              :class="actionIcon"
              @click="addSegmentTimePair(item)"
            >
              add
            </v-icon>
          </template>
          <span> {{ $t('actions.addBreak') }} </span>
        </v-tooltip>
        <v-tooltip
          v-if="timePairNotApprovedOrRejected(item.status)"
          top
          :disabled="isMobile()"
        >
          <template v-slot:activator="{ on }">
            <v-icon
              v-if="isLocationFeatureActive"
              v-on="on"
              :disabled="!hasLocationData(item)"
              :class="actionIcon"
              @click="viewTimePairMap(item)"
            >
              {{ hasLocationData(item) ? 'location_on' : 'location_off' }}
            </v-icon>
          </template>
          <span>{{$t('indicators.showGeoData')}}</span>
        </v-tooltip>
        <v-tooltip
          v-if="timePairNotApprovedOrRejected(item.status)"
          top
          :disabled="isMobile()"
        >
          <template v-slot:activator="{ on }">
            <v-icon
              v-show="!!item.clockOut"
              v-on="on"
              :class="actionIcon"
              @click="viewTimePair(item)"
            >
              {{ isTimePairEditFeatureActive ? 'history' : 'visibility' }}
            </v-icon>
          </template>
          <span> {{ isTimePairEditFeatureActive ?  $t('indicators.showHistory') : $t('indicators.showDetails') }}</span>
        </v-tooltip>
        <v-tooltip
          v-if="timePairNotApprovedOrRejected(item.status)"
          top
          :disabled="isMobile()"
        >
          <template v-slot:activator="{ on }">
            <v-icon
              v-if="isTimePairEditFeatureActive"
              v-show="!!item.clockOut && !item.isInactive"
              v-on="on"
              :class="actionIcon"
              @click="editTimePair(item)"
            >
              edit
            </v-icon>
          </template>
          <span> {{ $t('actions.edit')}}</span>
        </v-tooltip>
        <v-tooltip
          v-if="timePairNotApprovedOrRejected(item.status)"
          top
          :disabled="isMobile()"
        >
          <template v-slot:activator="{ on }">
            <v-icon
              v-show="isManagerClockingActive && !item.clockOut"
              v-on="on"
              :class="actionIcon"
              @click="clockOut(item)"
            >
              alarm_off
            </v-icon>
          </template>
          <span>{{  $t('clock.clockOut') }}</span>
        </v-tooltip>
        <v-tooltip
          v-if="timePairNotApprovedOrRejected(item.status)"
          top
          :disabled="isMobile()"
        >
          <template v-slot:activator="{ on }">
            <v-icon
              v-if="isTimePairApprovalFeatureActive"
              v-show="!isMobile() && !!item.clockOut"
              v-on="on"
              :class="actionIcon"
              @click="singleTimePairApproval(item, false)"
            >
              clear
            </v-icon>
          </template>
          <span> {{ $t('actions.reject') }} </span>
        </v-tooltip>
        <v-tooltip
          v-if="timePairNotApprovedOrRejected(item.status)"
          top
          :disabled="isMobile()"
        >
          <template v-slot:activator="{ on }">
            <v-icon
              v-if="isTimePairApprovalFeatureActive"
              v-show="!isMobile() && !!item.clockOut"
              v-on="on"
              :class="actionIcon"
              @click="singleTimePairApproval(item, true)"
            >
              done
            </v-icon>
          </template>
          <span>{{ $t('actions.approve') }}</span>
        </v-tooltip>

        <v-icon
          v-if="isMobile() && item.segmentTimePairs.length > 0"
          :class="actionIcon"
          @click="viewSegmentTimePairs(item)"
        >
          emoji_food_beverage
        </v-icon>

        <span
          v-if="(!isManagerClockingActive && !item.clockOut) && !isLocationFeatureActive"
          class="font-italic text--disabled"
        >
          Unavailable
        </span>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pb-4">
          <v-card
            v-if="!isMobile() && item.segmentTimePairs.length !== 0"
            flat
            :class="{ 'mb-2': item.startingWorkScheduleDay != null }"
          >
            <SegmentTimePairTable
              :headers="segmentTimeDataTableHeaders"
              :items="filterDeletedOvertimeSegments(item.segmentTimePairs)"
            >
              <template v-if="isSegmentTimePairFeatureActive" v-slot:actions="{ item: segmentTimePair }">
                <div
                  v-if="isTimePairEditFeatureActive && item.clockOut && segmentTimePair.clockOut
                  && !segmentTimePair.isInactive && !isOvertimeRelatedSegment(segmentTimePair)"
                >
                  <v-tooltip top :disabled="isMobile()">
                    <template v-slot:activator="{ on }">
                      <v-icon
                        v-on="on"
                        :class="actionIcon"
                        @click="editTimePair(item, segmentTimePair)"
                      >
                        edit
                      </v-icon>
                    </template>
                    <span>{{ $t('actions.editBreak') }}</span>
                  </v-tooltip>
                </div>
                <div
                  v-else
                  class="font-italic text--disabled"
                >
                  {{ $t('misc.unavailable') }}
                </div>
              </template>
            </SegmentTimePairTable>
          </v-card>
          <v-card tile v-if="isWorkScheduleManagerViewFeatureEnabled">
            <WorkScheduleCard :time-pair="item" v-if="item.startingWorkScheduleDay != null"/>
          </v-card>
        </td>
      </template>
      <template v-slot:footer.page-text="items">
        {{ items.pageStart }} - {{ items.pageStop }} {{ $t('misc.of') }} {{ items.itemsLength }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import getTableFooterProps from '@/mixins/getTableFooterProps';
import hasLocationData from '@/mixins/location/hasLocationData';
import getTimePairStatusIndicator from '@/mixins/timepairs/getTimePairStatusIndicator';
import getExpectedDuration from '@/mixins/getExpectedDuration';
import showTimePairDeviation from '@/mixins/showTimePairDeviation';
import getWorkScheduleDeviation from '@/mixins/getWorkScheduleDeviation';
import hasActionIcon from '@/mixins/hasActionIcon';
import hasLocationFeature from '@/mixins/location/hasLocationFeature';
import hasTimePairFeatures from '@/mixins/timepairs/hasTimePairFeatures';
import hasWorkScheduleFeatures from '@/mixins/hasWorkScheduleFeatures';
import getWorkScheduleExternalNameByCode from '@/mixins/getWorkScheduleExternalName';
import getActualStartOrEndTime from '@/mixins/timepairs/getActualStartOrEndTime';
import overtimeSegments from '@/mixins/overtimeSegments';
import EndTimeTableCell from '@/components/timepairs/shared/EndTimeTableCell.vue';
import StartDateTableCell from '@/components/timepairs/shared/StartDateTableCell.vue';
import StartTimeTableCell from '@/components/timepairs/shared/StartTimeTableCell.vue';

import SegmentTimePairTable from '@/components/timepairs/SegmentTimePairTable.vue';
import DateTimeUtil from '@/util/date-time-util';
import constants from '@/constants';
import TimePairStatusIndicator from '@/components/timepairs/TimePairStatusIndicator.vue';
import ApprovalStatusTableCell from '@/components/timepairs/shared/ApprovalStatusTableCell.vue';
import WorkScheduleCard from '@/components/timepairs/WorkScheduleCard.vue';
import AuthorisationUtil from '@/util/authorisation-util';
import find from 'lodash/find';

export default {
  name: 'ApprovalsTimePairTable',

  components: {
    ApprovalStatusTableCell,
    StartDateTableCell,
    StartTimeTableCell,
    EndTimeTableCell,
    SegmentTimePairTable,
    TimePairStatusIndicator,
    WorkScheduleCard,
  },

  mixins: [
    getExpectedDuration,
    getTableFooterProps,
    getTimePairStatusIndicator,
    hasWorkScheduleFeatures,
    getWorkScheduleDeviation,
    hasActionIcon,
    hasLocationData,
    hasLocationFeature,
    hasTimePairFeatures,
    showTimePairDeviation,
    getWorkScheduleExternalNameByCode,
    getActualStartOrEndTime,
    overtimeSegments,
  ],

  props: {
    username: {
      type: String,
      required: true,
    },
    viewAdjustedTime: {
      type: Boolean,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      segmentTimeDataTableHeaders: [
        {
          text: this.$t('tableHeaders.info'),
          value: constants.DataTableHeaderValues.INFO,
        },
        {
          text: this.$t('clock.startTime'),
          value: constants.DataTableHeaderValues.CLOCK_IN,
          align: 'center',
        },
        {
          text: this.$t('clock.endTime'),
          value: constants.DataTableHeaderValues.CLOCK_OUT,
          align: 'center',
        },
        {
          text: this.$t('tableHeaders.duration'),
          value: constants.DataTableHeaderValues.DURATION,
          align: 'center',
        },
        {
          text: this.$t('tableHeaders.actions'),
          value: constants.DataTableHeaderValues.ACTIONS,
          align: 'center',
        },
      ],
      sortBy: 'clockIn',
      sortDesc: false,
      selectedItems: [],
      expanded: [],
    };
  },

  computed: {
    ...mapState(['geofence', 'timePairSelectability']),
    ...mapGetters(['getApprovalsByUsername', 'getFilteredTimes']),

    isManagerClockingActive() {
      return AuthorisationUtil.managerCanClockEmployees();
    },
  },

  methods: {
    ...mapActions([
      'selectTimePair',
      'updateCurrentTimePairIds',
    ]),

    capitalize(string) {
      return capitalize(string);
    },

    getTimePairsPerPage() {
      if (this.isMobile()) {
        return constants.Approvals.TIME_PAIRS_PER_PAGE_MOBILE;
      }
      return constants.Approvals.TIME_PAIRS_PER_PAGE;
    },

    singleTimePairApproval(timePair, isApproved) {
      this.$emit('single-time-pair-approval', timePair, isApproved);
    },

    addSegmentTimePair(timePair) {
      this.$emit('add-segment', timePair);
    },

    editTimePair(shiftTimePair, segmentTimePair) {
      this.$emit('edit-time-pair', { shiftTimePair, segmentTimePair });
    },

    viewTimePair(timePair) {
      this.$emit('view-time-pair', timePair);
    },

    viewSegmentTimePairs(shiftTimePair) {
      this.$emit('view-segments', shiftTimePair);
    },

    viewTimePairMap(timePair) {
      if (this.hasLocationData(timePair)) {
        this.$emit('view-time-pair-map', timePair);
      }
    },

    clockOut(timePair) {
      this.$emit('clock-out', timePair);
    },

    handleTimePairSelection(timePair, value) {
      if (timePair.originalVersionId) {
        this.selectTimePair({ username: this.username, timePairId: timePair.originalVersionId, value });
      } else {
        this.selectTimePair({ username: this.username, timePairId: timePair.id, value });
      }
      value = true;
    },

    handleCurrentItemsChange(currentItems) {
      this.updateCurrentTimePairIds({
        currentItemIds: new Set(map(currentItems, (timePair) => timePair.id)),
        username: this.username,
      });
    },

    convertToDisplayedClockIn(dateTime) {
      return DateTimeUtil.convertToHumanReadableDateTime(dateTime);
    },

    convertToDisplayedClockOut(clockOut) {
      return DateTimeUtil.convertToHumanReadableDateTime(clockOut.date);
    },

    convertToDisplayedDateTime(dateTime) {
      return DateTimeUtil.convertToHumanReadableDateTime(dateTime);
    },

    convertToDisplayedDuration(timePair) {
      return DateTimeUtil.getFormattedDuration(timePair.roundedDuration);
    },

    getRowIndicators(timePair) {
      const indicators = this.getTimePairClockingTypeIndicators(timePair);

      if (this.isLocationFeatureActive && !this.hasLocationData(timePair)) {
        indicators.push(this.getIndicatorObject(
          'location', 'location_off', constants.AppColors.WARN, this.$t('indicators.noGeoData'),
        ));
      } else if (this.isLocationFeatureActive
        && !isEmpty(this.geofence.flatMap((geoFenceAssignment) => geoFenceAssignment.geofences))
        && (timePair.clockIn.geofenceResult !== constants.LocationData.GeofenceResult.INSIDE || (timePair.clockOut
            && timePair.clockOut.geofenceResult !== constants.LocationData.GeofenceResult.INSIDE))) {
        indicators.push(this.getIndicatorObject(
          'geofence', 'gps_off', constants.AppColors.WARN, this.$t('location.geoLocation.outsideGeoFence'),
        ));
      }

      if (!isEmpty(timePair.segmentTimePairs)) {
        if (find(timePair.segmentTimePairs, (seg) => seg.pairType === constants.WorkSchedule.SegmentType.BREAK)) {
          indicators.push(this.getIndicatorObject(
            'shift_breaks', 'emoji_food_beverage', constants.AppColors.PRIMARY, this.$t('indicators.containsBreaks'),
          ));
        }
        if (find(timePair.segmentTimePairs, (seg) => seg.pairType !== constants.WorkSchedule.SegmentType.BREAK)) {
          indicators.push(this.getIndicatorObject(
            'shift_overtime', 'alarm', constants.AppColors.PRIMARY, this.$t('indicators.containsOvertime'),
          ));
        }
      } else if (timePair.pairType !== 'SHIFT') {
        indicators.push(this.getIndicatorObject(
          'shift_overtime', 'alarm', constants.AppColors.PRIMARY, this.$t('indicators.containsOvertime'),
        ));
      }

      if (timePair.isInactive) {
        indicators.push(this.getIndicatorObject(
          'deleted',
          'delete',
          constants.AppColors.ERROR,
          timePair.deletedDate
            ? this.$t('indicators.timeDeletedOn',
              { time: DateTimeUtil.convertToHumanReadableDate(timePair.deletedDate) })
            : this.$t('indicators.pendingDelete'),
        ));
      }

      if (timePair.isAbsence) {
        indicators.push(this.getIndicatorObject(
          'absent',
          'alarm_off',
          constants.AppColors.WARN,
          this.$t('indicators.employeeAbsence', { type: timePair.absenceType }),
        ));
      }

      return indicators;
    },

    getTimePairsOrAdjustedTimePairs(timePairs) {
      if (this.viewAdjustedTime === true) {
        return map(timePairs, (pair) => (pair.clockOut == null ? pair : pair.adjustedVersion));
      }
      return timePairs;
    },

    timePairNotApprovedOrRejected(status) {
      return status !== constants.Approvals.Status.APPROVED && status !== constants.Approvals.Status.REJECTED;
    },
  },
};
</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
>>> .v-data-table__expanded.v-data-table__expanded__content {
  background-color: #f2f2f2;
  box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
  -webkit-box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
  -moz-box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
}

/*noinspection CssUnusedSymbol*/
>>> .v-data-table-header-mobile__select {
  display: none;
}
</style>
